import { cloneDeep } from "lodash";

import { getApi, postApi, putApi } from "../../../../../../apis/api";
import httpService from "../../../../../../apis/axios-config/http-service";
import {
  ADD_CONVERSATION_NOTE,
  CLAUDE_DRAFT,
  CREATE_CLAUDE_DRAFT,
  CREATE_CONVERSATION_THREAD,
  DOWNLOAD_CONVERSATION_ATTACHMENT,
  FILE_EMAIL_ATTACHMENT_TO_ORDER,
  GET_CONVERSATION_ACTIVITY,
  GET_HELPDESK_CANNED_RESPONSES,
  GET_ORDER_CONVERSATIONS,
  GET_TICKET_CONVERSATION,
  LOOKUP_TICKET,
  MARK_CONVERSATION_READ,
  MOVE_CONVERSATION_EMAIL,
  OPEN_CONVERSATION_THREAD,
  ORDER_CONVERSATIONS_UNREAD_SUFFIX,
  SEARCH_TICKET_RECIPIENTS,
  SEND_CONVERSATION_EMAIL,
  SPLIT_CONVERSATION_EMAIL,
  UPDATE_CONVERSATION_THREAD,
} from "../../../../../../apis/end-points";
import { DiscardDraftValues } from "../../../../../../components/shared/DiscardDraftModal";
import { CONVERSATION_MESSAGES } from "../../../../../../utils/message";
import { otherParty } from "../constants";
import {
  ActivityEntry,
  CannedResponse,
  ClaudeDraft,
  ConversationServiceResponse,
  ConversationsSnapshot,
  NewThreadEmail,
  OutgoingEmail,
  Party,
  RecipientOption,
  SendEmailResult,
  ThreadDetail,
  ThreadEmail,
  ThreadPatch,
  TicketConversationSnapshot,
} from "../types";
import { MOCK_CANNED_RESPONSES, buildMockSnapshot } from "./conversationMockData";

/**
 * Data access for the order Conversations tab — the order-side client of the
 * helpdesk mail service.
 *
 * Live (helpdesk-service): `fetchOrderConversations`, `fetchUnreadCount`,
 * `updateThread`, `sendEmail`, `openThread`, `markRead`, `addNote` and
 * `fetchActivity`.
 *
 * Every other call still resolves against an in-memory store through
 * `mockRequest`, one copy per order, until its endpoint exists. Each one names
 * the endpoint it maps to and returns the same envelope as `api.ts`, so going
 * live means swapping `mockRequest(ENDPOINT, …)` for `getApi` / `postApi` /
 * `putApi(ENDPOINT, …)` in this file only — state and components stay
 * untouched. The mock store does not hold the live threads, so those calls
 * answer "thread not found" for real tickets until they are wired up.
 */

const MOCK_LATENCY_MS = 300;
const DRAFT_LATENCY_MS = 900;

const store = new Map<string, ConversationsSnapshot>();
let idSequence = 0;

const ok = <T>(data: T): ConversationServiceResponse<T> => ({
  success: true,
  data: cloneDeep(data),
});
const fail = <T>(message: string): ConversationServiceResponse<T> => ({
  success: false,
  error: { message },
});

const mockRequest = <T>(
  _endpoint: string,
  resolve: () => ConversationServiceResponse<T>,
  latency = MOCK_LATENCY_MS
): Promise<ConversationServiceResponse<T>> =>
  new Promise((done) => setTimeout(() => done(resolve()), latency));

// ---- In-memory store helpers -------------------------------------------------

const nextId = (prefix: string) => {
  idSequence += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSequence}`;
};

const snapshotFor = (orderId: string): ConversationsSnapshot => {
  let snapshot = store.get(orderId);
  if (!snapshot) {
    snapshot = buildMockSnapshot(Date.now());
    store.set(orderId, snapshot);
  }
  return snapshot;
};

const findThread = (orderId: string, threadId: string) =>
  snapshotFor(orderId).threads.find((t) => t.id === threadId);

const findEmail = (orderId: string, emailId: string) => {
  for (const thread of snapshotFor(orderId).threads) {
    const email = thread.emails.find((e) => e.id === emailId);
    if (email) return { thread, email };
  }
  return undefined;
};

const nextTicketId = (orderId: string) => {
  const highest = snapshotFor(orderId).threads.reduce((max, t) => {
    const n = Number(t.ticketId.replace(/\D/g, ""));
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `TKT-${highest + 1}`;
};

const firstLine = (text: string, max = 70) => {
  const line = text.split("\n").find((l) => l.trim())?.trim() ?? "";
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
};

// ---- Calls ---------------------------------------------------------------------

// ---- Mock data source --------------------------------------------------------
// Screens whose tickets aren't in helpdesk-service run on the in-memory store:
// ConversationsProvider source="mock" (none today; Communications → Exceptions is live since KAN-1347).

export const fetchMockOrderConversations = (
  orderId: string
): Promise<ConversationServiceResponse<ConversationsSnapshot>> =>
  mockRequest(`${GET_ORDER_CONVERSATIONS}${orderId}`, () => ok(snapshotFor(orderId)));

export const updateMockThread = (
  orderId: string,
  threadId: string,
  patch: ThreadPatch
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  mockRequest(`${UPDATE_CONVERSATION_THREAD}${threadId}`, () => {
    const thread = findThread(orderId, threadId);
    if (!thread) return fail(CONVERSATION_MESSAGES.THREAD_NOT_FOUND);
    Object.assign(thread, patch);
    return ok(thread);
  });

export const addMockNote = (
  orderId: string,
  threadId: string,
  authorId: string,
  body: string,
  mentionIds: string[]
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  mockRequest(`${ADD_CONVERSATION_NOTE}${threadId}`, () => {
    const thread = findThread(orderId, threadId);
    if (!thread) return fail(CONVERSATION_MESSAGES.THREAD_NOT_FOUND);
    const team = snapshotFor(orderId).team;
    thread.notes.push({
      id: nextId("note"),
      authorId,
      createdAt: new Date().toISOString(),
      body,
      mentions: mentionIds.filter((id) => team.some((member) => member.id === id)),
    });
    return ok(thread);
  });

// ---- Live calls --------------------------------------------------------------

/** Every ticket on the order, with the team and ticket groups (one load for the tab). */
export const fetchOrderConversations = (
  orderId: string
): Promise<ConversationServiceResponse<ConversationsSnapshot>> =>
  getApi<ConversationsSnapshot>(`${GET_ORDER_CONVERSATIONS}${orderId}`);

/** One ticket (with its order's other tickets) for the Communications ticket screen (KAN-1334–1340). */
export const fetchTicketConversation = (
  ticketId: string
): Promise<ConversationServiceResponse<TicketConversationSnapshot>> =>
  getApi<TicketConversationSnapshot>(`${GET_TICKET_CONVERSATION}${ticketId}`);

/** To / CC suggestions: DTS users, or agents (by name, email or Agent ID) with their addresses. */
export const searchRecipients = async (q: string, kind: "user" | "agent"): Promise<RecipientOption[]> => {
  const response = await getApi<RecipientOption[]>(
    `${SEARCH_TICKET_RECIPIENTS}?q=${encodeURIComponent(q.trim())}&kind=${kind}`
  );
  return response.success && response.data ? response.data : [];
};

/** Changes a thread's status, priority, ticket group or owner. */
export const updateThread = (
  _orderId: string,
  threadId: string,
  patch: ThreadPatch
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  putApi<ThreadDetail>(`${UPDATE_CONVERSATION_THREAD}${threadId}`, patch);

/** A file as base64 (no data: prefix) for the JSON body. */
export const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Sends a reply / reply all / forward from the thread's group mailbox (KAN-1321 / 1323).
 * An email that can't be sent comes back saved as "Mail not sent" with `sent: false`.
 */
export const sendEmail = async (
  threadId: string,
  outgoing: OutgoingEmail,
  /** "user": From is the sender's own group mailbox (Communications ticket screen) */
  fromScope?: "user"
): Promise<ConversationServiceResponse<SendEmailResult>> =>
  postApi<SendEmailResult>(`${SEND_CONVERSATION_EMAIL}${threadId}`, {
    ...(await toEmailBody(outgoing)),
    inReplyTo: outgoing.inReplyTo,
    ...(fromScope ? { fromScope } : {}),
  });

/**
 * "New Thread": creates a ticket on the order and sends its first email (same rules as a reply).
 * A send that fails still creates the ticket, its email shown as "Mail not sent".
 */
export const createThread = async (
  orderId: string,
  outgoing: NewThreadEmail
): Promise<ConversationServiceResponse<SendEmailResult>> =>
  postApi<SendEmailResult>(`${CREATE_CONVERSATION_THREAD}${orderId}`, await toEmailBody(outgoing));

/** The JSON body of an email: addresses only, files as base64. */
const toEmailBody = async (outgoing: NewThreadEmail) => ({
  chain: outgoing.chain,
  to: outgoing.to.map((recipient) => recipient.address),
  cc: outgoing.cc.map((recipient) => recipient.address),
  subject: outgoing.subject,
  bodyText: outgoing.bodyText,
  ...(outgoing.bodyHtml ? { bodyHtml: outgoing.bodyHtml } : {}),
  mentionIds: outgoing.mentionIds,
  attachments: await Promise.all(
    outgoing.attachments.map(async (file) => ({
      fileName: file.name,
      contentType: file.type,
      contentBase64: await toBase64(file),
    }))
  ),
});

/** Mock data (source="mock"): the reply is added to the in-memory thread. */
export const sendMockEmail = (
  orderId: string,
  threadId: string,
  outgoing: OutgoingEmail,
  authorId: string
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  mockRequest(`${SEND_CONVERSATION_EMAIL}${threadId}`, () => {
    const thread = findThread(orderId, threadId);
    if (!thread) return fail(CONVERSATION_MESSAGES.THREAD_NOT_FOUND);
    if (!outgoing.to.length) return fail(CONVERSATION_MESSAGES.RECIPIENT_REQUIRED);

    const id = nextId("em");
    const email: ThreadEmail = {
      id,
      messageId: `<${id}@${thread.mailbox.address.split("@")[1]}>`,
      inReplyTo: outgoing.inReplyTo,
      chain: outgoing.chain,
      direction: "outbound",
      subject: outgoing.subject,
      from: { name: thread.mailbox.senderName, address: thread.mailbox.address },
      to: outgoing.to,
      cc: outgoing.cc,
      sentAt: new Date().toISOString(),
      bodyText: outgoing.bodyText,
      attachments: [],
      authorId,
      deliveryStatus: "sent",
    };
    thread.emails.push(email);
    return ok(thread);
  });

/**
 * Adds an internal note. `mentionIds` are the teammates @mentioned in it; the
 * service keeps only members of the ticket's group (any group when Unassigned).
 * The author is the logged-in user.
 */
export const addNote = (
  _orderId: string,
  threadId: string,
  _authorId: string,
  body: string,
  mentionIds: string[]
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  postApi<ThreadDetail>(`${ADD_CONVERSATION_NOTE}${threadId}`, { body, mentionIds });

/** Master search by ticket number (KAN-1320): where the ticket is. */
export interface TicketLocation {
  ticketId: string;
  ticketNumber: string;
  subject: string;
  orderId: string | null;
  orderNumber: string | null;
}

export const lookupTicket = (
  ticketNumber: string
): Promise<ConversationServiceResponse<TicketLocation>> =>
  getApi<TicketLocation>(`${LOOKUP_TICKET}${encodeURIComponent(ticketNumber.trim())}`);

/** The user opened the thread from the list — logged as "X opened the ticket". */
export const openThread = (
  threadId: string
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  postApi<ThreadDetail>(`${OPEN_CONVERSATION_THREAD}${threadId}`, {});

/** The user opened these emails / notes — clears them from their unread counts. */
export const markRead = (
  threadId: string,
  messageIds: string[]
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  postApi<ThreadDetail>(`${MARK_CONVERSATION_READ}${threadId}`, { messageIds });

/** The thread's activity log, newest first. */
export const fetchActivity = (
  threadId: string
): Promise<ConversationServiceResponse<ActivityEntry[]>> =>
  getApi<ActivityEntry[]>(`${GET_CONVERSATION_ACTIVITY}${threadId}`);

/** An email attachment's file (received attachments are read from the stored email). */
export const downloadAttachment = async (attachmentId: string): Promise<Blob> =>
  (await httpService.get(`${DOWNLOAD_CONVERSATION_ATTACHMENT}${attachmentId}`, {
    responseType: "blob",
  })) as Blob;

/** Unread received emails on the order for the logged-in user (the tab badge). */
export const fetchUnreadCount = (
  orderId: string
): Promise<ConversationServiceResponse<{ unreadCount: number }>> =>
  getApi<{ unreadCount: number }>(
    `${GET_ORDER_CONVERSATIONS}${orderId}${ORDER_CONVERSATIONS_UNREAD_SUFFIX}`
  );

/** Moves an email filed on the wrong chain to the other one. */
export const moveEmailToOtherChain = (
  orderId: string,
  emailId: string
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  mockRequest(`${MOVE_CONVERSATION_EMAIL}${emailId}`, () => {
    const found = findEmail(orderId, emailId);
    if (!found) return fail(CONVERSATION_MESSAGES.EMAIL_NOT_FOUND);
    found.email.chain = otherParty(found.email.chain);
    return ok(found.thread);
  });

/**
 * Lifts an email into a ticket of its own — e.g. a second question asked in
 * the same email. Returns both the trimmed source thread and the new one.
 */
export const splitEmail = (
  orderId: string,
  emailId: string,
  ownerId: string
): Promise<ConversationServiceResponse<{ source: ThreadDetail; created: ThreadDetail }>> =>
  mockRequest(`${SPLIT_CONVERSATION_EMAIL}${emailId}`, () => {
    const found = findEmail(orderId, emailId);
    if (!found) return fail(CONVERSATION_MESSAGES.EMAIL_NOT_FOUND);
    const { thread: source, email } = found;

    const created: ThreadDetail = {
      ...cloneDeep(source),
      id: nextId("thread"),
      ticketId: nextTicketId(orderId),
      subject: firstLine(email.bodyText),
      status: "open",
      ownerId,
      dueLabel: undefined,
      emails: [{ ...email, inReplyTo: null }],
      notes: [],
      presence: [],
      pendingDrafts: [],
    };
    source.emails = source.emails.filter((e) => e.id !== emailId);
    snapshotFor(orderId).threads.unshift(created);
    return ok({ source, created });
  });

/** Copies an email attachment onto the order's documents. */
export const fileAttachmentToOrder = (
  orderId: string,
  emailId: string,
  attachmentId: string
): Promise<ConversationServiceResponse<ThreadDetail>> =>
  mockRequest(`${FILE_EMAIL_ATTACHMENT_TO_ORDER}${attachmentId}`, () => {
    const found = findEmail(orderId, emailId);
    const attachment = found?.email.attachments.find((a) => a.id === attachmentId);
    if (!found || !attachment) return fail(CONVERSATION_MESSAGES.EMAIL_NOT_FOUND);
    attachment.filedToOrder = true;
    return ok(found.thread);
  });

export const fetchCannedResponses = (): Promise<
  ConversationServiceResponse<CannedResponse[]>
> => mockRequest(GET_HELPDESK_CANNED_RESPONSES, () => ok(MOCK_CANNED_RESPONSES));

/**
 * "Draft with Claude" (helpdesk-service): Claude drafts a reply on the chain for the teammate to review, or hands
 * it back to a person with its reason. Nothing is sent until the teammate sends it.
 */
export const draftWithClaude = (
  threadId: string,
  chain: Party
): Promise<ConversationServiceResponse<ClaudeDraft>> =>
  postApi<ClaudeDraft>(`${CREATE_CLAUDE_DRAFT}${threadId}`, { chain });

/**
 * Saves the composer's edits to the Claude draft, so a POD agent's draft shows the same text on its POD request
 * screen. Saving an edit teaches Claude nothing; sending or discarding does.
 */
export const updateClaudeDraft = (
  draftId: string,
  patch: { subject: string; bodyText: string; bodyHtml: string; to: string[]; cc: string[] }
): Promise<ConversationServiceResponse<ClaudeDraft>> =>
  putApi<ClaudeDraft>(`${CLAUDE_DRAFT}${encodeURIComponent(draftId)}`, patch);

/** Regenerate: a new draft for the same email (the old one is kept as replaced). */
export const regenerateClaudeDraft = (draftId: string): Promise<ConversationServiceResponse<ClaudeDraft>> =>
  postApi<ClaudeDraft>(`${CLAUDE_DRAFT}${draftId}/regenerate`, {});

/** Discard ("Why are you discarding this draft?"); with no reason Claude learns nothing from it. */
export const discardClaudeDraft = (
  draftId: string,
  values: DiscardDraftValues
): Promise<ConversationServiceResponse<ClaudeDraft>> =>
  postApi<ClaudeDraft>(`${CLAUDE_DRAFT}${draftId}/discard`, values);

/**
 * Sends a Claude draft as the teammate wrote it in the composer (their edits included), through the same path
 * as any reply and signed by them.
 */
export const sendClaudeDraft = async (
  draftId: string,
  outgoing: OutgoingEmail,
  fromScope?: "user"
): Promise<ConversationServiceResponse<SendEmailResult>> => {
  const { chain, ...body } = await toEmailBody(outgoing);
  return postApi<SendEmailResult>(`${CLAUDE_DRAFT}${draftId}/send`, {
    ...body,
    ...(fromScope ? { fromScope } : {}),
  });
};

/** Mock data (source="mock"): a short reply to the latest inbound email on the chain. */
export const draftMockReply = (
  orderId: string,
  threadId: string,
  chain: Party
): Promise<ConversationServiceResponse<string>> =>
  mockRequest(
    `${CREATE_CLAUDE_DRAFT}${threadId}`,
    () => {
      const thread = findThread(orderId, threadId);
      if (!thread) return fail(CONVERSATION_MESSAGES.THREAD_NOT_FOUND);
      const latestInbound = [...thread.emails]
        .reverse()
        .find((e) => e.chain === chain && e.direction === "inbound");
      const chainParticipants =
        (chain === "agentLtl"
          ? thread.chains.agentLtl
          : chain === "origin"
            ? thread.chains.origin
            : chain === "consignee"
              ? thread.chains.consignee
              : thread.chains[chain]
        )?.participants ?? [];
      const recipient = latestInbound?.from ?? chainParticipants[0];
      const greeting = `Hi ${(recipient?.name ?? "there").split(" ")[0]},`;
      const { orderNumber } = thread.order;

      return ok(
        chain === "customer"
          ? `${greeting}\n\nThanks for your patience on ${orderNumber}. We've confirmed the details with the carrier and will update you with the delivery plan shortly.`
          : `${greeting}\n\nThanks for the update on ${orderNumber}. Please go ahead as discussed and confirm once it's booked.`
      );
    },
    DRAFT_LATENCY_MS
  );

/** Test-only: forget every order's conversations. */
export const resetConversationStore = () => store.clear();
